import { Link } from 'react-router-dom'
import PremiumHeader from '../components/PremiumHeader'
import { useAuth } from '../lib/auth-context'

type ModuleCard = {
  title: string
  description: string
  links: Array<{ to: string; label: string; tone?: 'primary' | 'secondary' }>
}

const moduleCards: ModuleCard[] = [
  {
    title: 'Orders & Quotes',
    description: 'Open the order desk, quote desk, and public QuotePort entry points from one place.',
    links: [
      { to: '/orders', label: 'Orders', tone: 'primary' },
      { to: '/quote-desk', label: 'Quotes', tone: 'primary' },
      { to: '/q/wahatoya-auto-group', label: 'QuotePort Demo', tone: 'secondary' },
    ],
  },
  {
    title: 'Customer Records',
    description: 'Manage customer accounts, locations, contacts, and billing profile context.',
    links: [
      { to: '/customers', label: 'Customer Accounts', tone: 'primary' },
      { to: '/places', label: 'Locations / Contacts', tone: 'primary' },
      { to: '/customers/new', label: 'New Customer', tone: 'secondary' },
    ],
  },
  {
    title: 'Operations',
    description: 'Run loads, trailer planning, driver views, and trip work from the internal shell.',
    links: [
      { to: '/loads', label: 'Trips / Load Plans', tone: 'primary' },
      { to: '/driver/jobs', label: 'Driver Views', tone: 'primary' },
      { to: '/driver/trailer-loader', label: 'Trailer Loader', tone: 'secondary' },
    ],
  },
  {
    title: 'Carrier Management',
    description: 'Review carriers, driver assignments, equipment, and fleet finance workflows.',
    links: [
      { to: '/carriers', label: 'Carriers', tone: 'primary' },
      { to: '/carrier/drivers', label: 'Drivers', tone: 'primary' },
      { to: '/carrier/equipment', label: 'Equipment', tone: 'secondary' },
      { to: '/carrier/fleet/finance', label: 'Fleet Finance', tone: 'secondary' },
    ],
  },
  {
    title: 'Inspections & Docs',
    description: 'Surface vehicle inspections, condition triage, and the document workflow tied to loads and orders.',
    links: [
      { to: '/inspect/530dcf6c0e6712d827713477cb17c14927de3af50d989bb7', label: 'Vehicle Inspections', tone: 'primary' },
      { to: '/orders', label: 'Condition Issue Triage', tone: 'secondary' },
      { to: '/loads', label: 'Documents', tone: 'secondary' },
    ],
  },
  {
    title: 'Tools',
    description: 'Internal admin utilities, imports, and demo data helpers stay available here.',
    links: [
      { to: '/demo-status', label: 'Demo Data', tone: 'primary' },
      { to: '/imports', label: 'Internal Tools', tone: 'primary' },
      { to: '/settings', label: 'Settings', tone: 'secondary' },
    ],
  },
]

function LinkChip({ to, label, tone = 'secondary' }: { to: string; label: string; tone?: 'primary' | 'secondary' }) {
  const toneClasses = tone === 'primary'
    ? 'border-cyan-400/40 bg-cyan-400/10 text-cyan-50 hover:border-cyan-300/70 hover:bg-cyan-400/15'
    : 'border-white/10 bg-white/5 text-slate-200 hover:border-white/20 hover:bg-white/10'

  return (
    <Link to={to} className={`rounded-full border px-3 py-2 text-sm font-medium transition ${toneClasses}`}>
      {label}
    </Link>
  )
}

function ModulePanel({ title, description, links }: ModuleCard) {
  return (
    <section className="rounded-3xl border border-white/10 bg-slate-950/50 p-5 shadow-xl shadow-black/20 backdrop-blur-xl">
      <div className="text-xs uppercase tracking-[0.3em] text-cyan-200/70">{title}</div>
      <p className="mt-2 text-sm leading-6 text-slate-300">{description}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {links.map((link) => <LinkChip key={link.to + link.label} {...link} />)}
      </div>
    </section>
  )
}

export default function AdminHomePage() {
  const { user } = useAuth()

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -left-24 top-16 h-80 w-80 rounded-full bg-cyan-500/15 blur-3xl" />
        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-amber-400/10 blur-3xl" />
      </div>

      <main className="relative mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <PremiumHeader
          eyebrow="Internal workspace"
          title="Admin / Owner home"
          subtitle="Full-access landing page for internal operators, owners, and demo administrators."
          primaryAction={<Link to="/dispatcher" className="rounded-full border border-cyan-400/40 bg-cyan-400/10 px-4 py-2 text-sm font-semibold text-cyan-50 transition hover:bg-cyan-400/15">Open Command Center</Link>}
          secondaryAction={<Link to="/loads" className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-white/10">Open Loads</Link>}
        />

        <div className="mt-4 rounded-3xl border border-white/10 bg-white/5 p-4 text-sm text-slate-300 backdrop-blur-xl">
          Signed in as <span className="font-semibold text-cyan-100">{user?.name ?? user?.email ?? 'Internal user'}</span> ·{' '}
          <span className="font-semibold text-slate-100">{user?.accountType ?? 'UNKNOWN'}</span>
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          {moduleCards.map((card) => <ModulePanel key={card.title} {...card} />)}
        </div>
      </main>
    </div>
  )
}